"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Building2, Newspaper, Layers, ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function InternationalAuthority() {
  const comparisonItems = [{
    type: "misconception",
    badge: "The Common Misconception",
    title: "The Domestic Authority Assumption",
    description: "Many organisations assume that because their primary domain holds strong authority and top rankings in their domestic market, international subfolders or ccTLDs will rank effortlessly in new territories.",
    icon: AlertCircle,
    points: ["Search engines evaluate regional search intent and local topical context independently.", "Domestic backlinks carry weaker geographical relevance when competing against native regional websites.", "Authority earned in one country does not automatically transfer to another market without local signals."]
  }, {
    type: "solution",
    badge: "Targeted In-Market Signals",
    title: "Building Authentic Regional Entity Trust",
    description: "When expanding into new territories, in-country authority must be earned from genuine local sources rather than artificially replicating bulk link profiles.",
    icon: ShieldCheck,
    points: ["Editorial links acquired from publications and websites rooted within the target country.", "Verifiable regional business citations, local partnerships, and relevant local directories.", "Consistent brand details, contact information, and business registrations across international markets."]
  }];
  const pillars = [{
    title: "Relevance and Editorial Quality",
    description: "Volume-led and paid-link tactics do not build sustainable search trust. In new international markets, contextual editorial mentions from recognised industry leaders in that specific country deliver far greater credibility than hundreds of generic global links.",
    icon: Layers,
    highlight: "Editorial Quality"
  }, {
    title: "Local Partnerships and Directories",
    description: "Search engines look for corroborating signals that a business genuinely serves a region. Relevant local trade directories, chamber memberships, and commercial partnerships help anchor regional presence.",
    icon: Building2,
    highlight: "Local Relevance"
  }, {
    title: "Digital PR and Thought Leadership",
    description: "To compete effectively against established domestic businesses, a brand needs authentic regional media exposure. Targeted digital PR campaigns, localised thought leadership, and expert contributions to regional industry publications earn meaningful coverage.",
    icon: Newspaper,
    highlight: "Regional Coverage"
  }];
  return <section id="internationallinkbuildingauthority" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32 border-b border-border selection:bg-accent selection:text-accent-foreground">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-sans text-xs tracking-wide">
              Market Authority
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-[1.15] mb-6">
            International Link Building and Authority
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            One of the most persistent misconceptions during international expansions is the expectation that domestic domain authority automatically carries into foreign markets. It does not. Search engines evaluate contextual in-country relevance and local brand signals before awarding competitive organic visibility.
          </p>
        </motion.div>

        {/* 2-Column Comparative Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {comparisonItems.map((item, i) => {
          const Icon = item.icon;
          const isMisconception = item.type === "misconception";
          return <motion.div key={item.title} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: "easeOut"
          }} className="h-full">
                <Card className={`h-full border transition-all duration-300 shadow-xs hover:shadow-md ${isMisconception ? "border-border/80 bg-card" : "border-primary/20 bg-secondary/20"}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm uppercase tracking-wider ${isMisconception ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}`}>
                        {item.badge}
                      </span>
                      <Icon className={`size-5 ${isMisconception ? "text-muted-foreground" : "text-primary"}`} strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground pt-1 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <ul className="space-y-3.5">
                      {item.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-3">
                          {isMisconception ? <span className="size-1.5 rounded-full bg-muted-foreground/60 mt-2 shrink-0" /> : <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />}
                          <span className="text-sm text-foreground/90 leading-relaxed font-sans">
                            {pt}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* 3-Column Pillars of Entity Authority */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillars.map((pillar, i) => {
          const PillarIcon = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2 + i * 0.1,
            ease: "easeOut"
          }} className="h-full">
                <div className="group h-full flex flex-col justify-between p-6 sm:p-8 rounded-xl border border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-sm transition-all duration-300">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary">
                        <PillarIcon className="size-6" strokeWidth={1.5} />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground tracking-tight">
                        {pillar.highlight}
                      </span>
                    </div>

                    <h3 className="text-lg sm:text-xl font-serif font-semibold text-foreground mb-3 leading-snug">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-border/60">
                    <span className="text-xs text-foreground/80 font-medium inline-flex items-center gap-1.5">
                      Editorial quality focus
                    </span>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Closing Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-card/60 p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl space-y-2">
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
              Need a clear authority strategy for your foreign target markets?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              I audit regional link profiles and establish in-country credibility strategies focused on editorial quality, relevant industry publications, and verifiable local business presence.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-sans font-medium">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-sans">
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}